/* ── CPL v5 §4 P4 — 네이버 검색광고 «전환» 발화 (2026-09-23) ──────────────────
 *
 * ── 왜 ──
 * 네이버 검색광고 보고서의 전환이 «0» 이다. 그래서 검색어별 성과를 노출·CTR 이라는
 * 대리지표로만 읽고 있고, 정작 광고가 만든 리드(30일 12건 · 전부 channel='naver_ad')가
 * 어느 «검색어» 에서 왔는지는 볼 수 없다. 배선하면 다음 검색어 보고서부터 직접 찍힌다.
 *
 * ── 실측 (2026-09-23) ──
 * https://wcs.naver.net/wcslog.js (v0.8.32) 를 «내려받아 읽었다». 문서 요약이 아니다.
 *   · 전송처   sendBeacon → https://wcs.naver.com/b   (beacon 불가 시 1×1 이미지 → /m?…)
 *   · NAC 모듈 wcslog 가 https://ssl.pstatic.net/melona/libs/gfp-nac-module/synchronizer.js
 *              를 «스크립트로» 덧붙이고, 그 모듈이 XHR 로 nam.veta.naver.com ·
 *              tivan.naver.com 을 부른다. → CSP 는 middleware.ts 한 곳에서 열었다.
 *   · wcs.trans(_conv) 가 신(trans) 규격이고 이 파일에 «실재» 한다.
 *     ⛔ 구(cnv) 규격과 섞지 않는다 — 신 전환이 한 번 들어오면 같은 유형의 구 전환은
 *        «영구 필터링» 된다(공식 가이드). 그래서 이 저장소는 trans «만» 쓴다.
 *   · trans 는 _conv.id 가 있으면 하루짜리 쿠키로 같은 `type|id` 재전송을
 *     `duplicate_<type>` 으로 바꿔 보낸다. 벤더 쪽 중복 방지가 «하루» 라는 뜻이다.
 *
 * ── 중복 발화 («재문의 포함 1회») ──
 * ⚠️ leadRef 는 폼 인스턴스마다 새로 난다(LeadForm.tsx:432). 그래서 벤더의 id 쿠키는
 *    «재문의» 를 못 막는다 — 같은 사람이 다시 넣으면 id 가 달라 새 전환으로 들어간다.
 * ⚠️ 서버(fn_insert_lead)는 dedupe_key(이름+전화) 충돌 시 «새 리드를 만들지 않고»
 *    inquiry_count 만 올린다(LeadForm.tsx:178~183). 즉 재문의는 리드가 «하나» 다.
 *    현장이 달라도 마찬가지다 — 그래서 아래 잠금은 현장별이 아니라 «브라우저 단위» 다.
 * 그래서 우리 쪽 잠금을 하나 더 둔다. 창은 벤더가 스스로 쓰는 값(하루)과 «같게» 맞췄다.
 * ⛔ 영구 잠금으로 만들지 않는다 — 몇 주 뒤 새 광고 클릭으로 들어온 사람의 전환까지
 *    조용히 버리게 되고, 그건 검색어 판정을 «과소» 쪽으로 틀어 버린다. 과다와 과소는
 *    둘 다 틀린 판정이다. 창을 바꾸려면 이 상수 한 곳만 고친다.
 */

/** 벤더의 id 쿠키 수명과 «같은» 값. wcslog.js 의 `c.setDate(a.getDate()+1)` 에서 왔다. */
const GUARD_MS = 24 * 60 * 60 * 1000;
const GUARD_KEY = 'kd_nvcnv';

/**
 * 전환 유형. 공식 유형표의 `lead`(상담/문의 신청)가 우리 리드폼의 뜻과 «정확히» 같다.
 * ⛔ 값을 바꾸지 말 것 — 네이버 콘솔의 전환유형 설정과 한 쌍이고, 어긋나면 보고서에
 *    전환이 다시 0 으로 보인다(우리 쪽은 정상 발화 중인데도).
 */
const CONV_TYPE = 'lead';

type WcsTransArg = {
  type: string;
  id?: string;
  value?: string | number;
  currency?: string;
  ext?: string;
};

/** window.wcs 는 외부 스크립트가 심는다 — 전역 d.ts 를 건드리지 않고 여기서만 좁게 본다. */
type WcsWindow = Window & {
  wcs?: { trans?: (conv: WcsTransArg) => boolean };
  wcs_add?: Record<string, string>;
};

/**
 * 'fired'       wcs.trans 를 실제로 불렀다
 * 'duplicate'   우리 쪽 잠금에 걸렸다(재문의 등) — 의도된 «안 보냄»
 * 'unavailable' 이 시점에 wcslog 가 없다(env 미설정·네트워크·차단). 뒤늦게 한 번 더 시도한다.
 */
export type NaverCnvResult = 'fired' | 'duplicate' | 'unavailable';

function guarded(): boolean {
  try {
    const at = Number(localStorage.getItem(GUARD_KEY) || 0);
    return at > 0 && Date.now() - at < GUARD_MS;
  } catch {
    /* 프라이빗 모드 등 — 잠금을 «포기» 한다. 전환을 못 보내는 것보다 낫다. */
    return false;
  }
}

function lock() {
  try {
    localStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    /* 저장 불가 — 위와 같은 판단 */
  }
}

function send(leadRef: string): boolean {
  const w = window as WcsWindow;
  if (typeof w.wcs?.trans !== 'function') return false;
  /* ⚠️ 「스크립트가 떴다」와 「초기화가 끝났다」는 다른 사실이다. wcslog 는 로드 즉시
     window.wcs 를 심고, wa·inflow 는 NaverWcs 의 onLoad 가 «그 뒤에» 채운다. 그 사이에
     쏘면 wcslog 가 wa 를 'unknown' 으로 채워 보내고(실측: `wcs_add.wa||"unknown"`),
     NA_SA 쿠키도 아직 없어 «귀속 없는 전환» 이 된다 — 보고서엔 뜨는데 검색어가 안 붙는
     가장 나쁜 형태다. 그래서 wa 가 실제로 박혔는지를 준비 완료의 신호로 본다. */
  if (!w.wcs_add?.wa) return false;
  try {
    /* ⚠️ value 를 싣지 않는다. 리드 한 건의 «금액» 은 우리가 모르는 값이고,
       지어낸 수치가 보고서의 전환매출로 굳으면 나중에 되돌릴 수 없다. */
    w.wcs.trans({ type: CONV_TYPE, id: leadRef });
    return true;
  } catch {
    return false;
  }
}

/** 로딩이 늦은 경우를 위한 짧은 재시도. 300ms × 10 = 3초까지만 본다. */
const RETRY_MS = 300;
const RETRY_MAX = 10;

function retry(leadRef: string, left: number) {
  if (left <= 0) return;
  setTimeout(() => {
    if (guarded()) return;
    if (send(leadRef)) lock();
    else retry(leadRef, left - 1);
  }, RETRY_MS);
}

/**
 * 리드 접수 «성공(recorded)» 시 한 번 부른다.
 * ⛔ silent-drop(허니팟=봇) · rejected · failed 에서 부르지 않는다 — 호출부가 지킨다.
 * ⚠️ 동기 반환이다. 계측 이벤트(lead_form_submit)를 이것 때문에 늦추지 않기 위해서다.
 *    'unavailable' 을 받아도 뒤에서 한 번 더 시도한다.
 */
export function fireNaverLeadConversion(leadRef: string): NaverCnvResult {
  if (typeof window === 'undefined') return 'unavailable';
  if (guarded()) return 'duplicate';
  if (send(leadRef)) {
    lock();
    return 'fired';
  }
  retry(leadRef, RETRY_MAX);
  return 'unavailable';
}
